import accountService from "@/services/account"
import tokenStorage from "@/utilities/TokenStorage"
import moment from "moment"
import logger from "@/utilities/logger"
import CrudClient from "@/services/CrudClient/";
import router from "@/router"
import Vue from "vue"

import * as Sentry from "@sentry/browser";

export default {
	state: {
		profile: null,
		selectedApplicationId: "C091A4A5-E083-4D76-A1A1-9ABA0210A031",
		isAuthenticated: false,
		authToken: null,
		tokenExpireTime: null,
		UserPublicProfiles: {},
		sasToken: null,
		utmParameters: null
	},
	mutations: {
		SET_PROFILE: (state, profile) => {
			state.profile = profile;
		},

		SET_SAS_TOKEN: (state, sasToken) => {
			state.sasToken = sasToken;
		},

		SET_AUTH_TOKEN: (state, authToken) => {
			logger.$log.info("setting auth token");

			state.authToken = authToken;
			const expireTime = moment.utc().add(authToken.expires_in, "seconds");
			state.tokenExpireTime = expireTime;
		},

		SIGN_OUT: (state) => {
			state.profile = null;
			state.isAuthenticated = false;
			state.authToken = null;
			state.tokenExpireTime = null;
		},

		IS_AUTHENTICATED: (state, isAuthenticated) => {
			state.isAuthenticated = isAuthenticated;
		},

		ADD_USER_PUBLIC_PROFILE: (state, profile) => {
			Vue.set(state.UserPublicProfiles, profile.Id, profile);
		},
	},
	actions: {

		SetRefreshToken(context, token) {
			context.commit("SET_AUTH_TOKEN", {
				refreshToken: token
			});
		},

		async GetSasToken(context) {
			const service = new CrudClient("StorageSasTokenController");

			const sasToken = await service.Get(null);
			context.commit("SET_SAS_TOKEN", sasToken);
		},

		async LoadUserData(context) {
			logger.$log.debug("Start: LoadUserData" + !!context.getters.getAuthToken);

			if (!context.getters.getAuthToken) {
				Sentry.configureScope((scope) => {
					scope.setUser();
				});
				return;
			}

			let data;
			try {
				data = await accountService.loadUserDetails();

				logger.$log.debug(data);
			} catch (err) {
				logger.$log.error("Cannot load user details");
				logger.$log.error(err);
				return;
			}

			context.commit("SET_PROFILE", data);

			context.commit("IS_AUTHENTICATED", true);

			// await context.dispatch("GetSasToken");

			logger.$log.debug("Sentry.configureScope");

			Sentry.configureScope(function(scope) {
				scope.setUser({
					id: data.AspNetUser.Id,
					username: data.AspNetUser.UserName
				});
			});

			// if the chat module is available
			if (context.rootState.chat && !context.getters.SignalRConnection) {
				// await is removed intentionally to set up chat in the background
				if (context.getters.isUserInRoles(["Nutritionist", "Patient"])) {
					context.dispatch("CreateSignalRConnection");
				}
			}
		},

		async Signin(context, {
			username,
			password,
			rememberMe,
			applicationId
		}) {
			const authResult = await accountService.Login(username, password);

			if (!authResult.error) {
				await context.dispatch("SignOut");
				tokenStorage.persistAuthResult(authResult, rememberMe);
				context.commit("SET_AUTH_TOKEN", authResult);

				// it's not possible to call an action within an action directly
				// await this.LoadUserData();
				await context.dispatch("LoadUserData", applicationId);
			}

			return authResult;
		},

		async SignInFromOAuth(context, { applicationId, token }) {
			logger.$log.info("SignInFromOAuth");

			await context.dispatch("SignOut");
			tokenStorage.persistAuthResult(token, true);
			context.commit("SET_AUTH_TOKEN", token);

			// it's not possible to call an action within an action directly
			// await this.LoadUserData();
			await context.dispatch("LoadUserData", applicationId);
		},

		// handle redirect where it is called
		async RefreshAccessToken(context, loadTokenFromStorage) {
			if (loadTokenFromStorage) {
				logger.$log.info("RefreshAccessToken loadTokenFromStorage");

				// get token from storage to store
				const token = tokenStorage.getAuthToken();

				if (token) {
					context.commit("SET_AUTH_TOKEN", token);
				} else { return false; }
			}

			// if there is no token available do not ask for a refresh token
			if (!context.getters.getAuthToken) {
				return false;
			}
			const authResult = await accountService.refreshAccessToken(context.getters.getAuthToken.refreshToken);

			if (authResult.error) {
				await context.dispatch("SignOut");
				return false;
			} else {
				tokenStorage.persistAuthResult(authResult);
				context.commit("SET_AUTH_TOKEN", authResult);
				return true;
			}
		},

		async refreshTokenFromInstantLoginToken(context, instantLoginToken) {
			try {
				const token = await accountService.refreshTokenFromInstantLoginToken(instantLoginToken);
				// it's not possible to call an action within an action directly
				await context.dispatch("SetRefreshToken", token.refreshToken);
				await context.dispatch("RefreshAccessToken", false);
			} catch (error) {
				logger.$log.error(error);

				router.push("/Login?InvalidAccessToken=true")
			}
		},

		async SignOut(context) {
			context.commit("SIGN_OUT");
			tokenStorage.clearAuthToken();

			// if the chat module is available
			if (context.rootState.chat) { await context.dispatch("ShutdownSignalRConnection"); }
		},

		async LoadUserPublicProfile(context, id) {
			try {
				if (context.state.UserPublicProfiles[id]) { return context.state.UserPublicProfiles[id]; }

				const PublicProfileService = new CrudClient("UserManagementExtensions/Users");

				const publicProfile = await PublicProfileService.GetCustom(
					`PublicProfile/${id}`,
					false
				);

				context.commit("ADD_USER_PUBLIC_PROFILE", publicProfile);
			} catch (err) {
				logger.$log.error(err);
			}
		}

	},
	getters: {
		UserPublicProfiles: (state) => {
			return state.UserPublicProfiles;
		},

		SasToken: (state) => {
			return state.sasToken;
		},

		UtmParameters: (state) => {
			return state.utmParameters;
		},

		QuestionnairePageQueryString: (state) => {
			if (!state.utmParameters) { return null; }

			let queryString = "";

			if (state.utmParameters.utmSource) {
				queryString += ((queryString === "") ? "" : "&") + "utm_source=" + state.utmParameters.utmSource;
			}

			if (state.utmParameters.utmMedium) {
				queryString += ((queryString === "") ? "" : "&") + "utm_medium=" + state.utmParameters.utmMedium;
			}

			if (state.utmParameters.utmCampaign) {
				queryString += ((queryString === "") ? "" : "&") + "utm_campaign=" + state.utmParameters.utmCampaign;
			}

			if (state.utmParameters.utmContent) {
				queryString += ((queryString === "") ? "" : "&") + "utm_content=" + state.utmParameters.utmContent;
			}

			return queryString;
		},

		selectedApplicationId: state => {
			return state.selectedApplicationId;
		},

		// SelectedApplication: state => {
		// 	return null;
		// },

		UserProfile: state => {
			return state.profile;
		},

		isUserInRole: (state) => (role) => {
			if (!state.profile) { return false; }

			const appRoles = state.profile.AspNetUser.ApplicationUsers.find(au => au.ApplicationId.toLowerCase() === state.selectedApplicationId.toLowerCase());

			return !!appRoles
				.ApplicationUserRoles
				.find(r => r.Role.Name === role);
		},

		isUserInRoles: (state) => (roles) => {
			if (!state.profile) { return false; }
			const appRoles = state.profile.AspNetUser.ApplicationUsers.find(au => au.ApplicationId.toLowerCase() === state.selectedApplicationId.toLowerCase());

			for (let i = 0; i < roles.length; i++) {
				const role = appRoles
					.ApplicationUserRoles
					.find(r => r.Role.Name === roles[i]);

				if (role) {
					return true;
				}
			}

			return false;
		},

		isUserAuthenticated: (state) => {
			return state.isAuthenticated;
		},

		getAuthToken: (state) => {
			return state.authToken;
		},

		getAuthHeader: (state) => {
			if (state.authToken) {
				return {
					Authorization: "Bearer " + state.authToken.accessToken
				};
			} else { return {}; }
		},

		isAccessTokenValid: (state) => {
			const now = moment.utc();
			return now.isBefore(state.tokenExpireTime);
		}

	}
}
